import { gql, useMutation } from '@apollo/client';
import { maybeRestErrorMessage } from 'modules/campaigns/editor/utils';
import { DEFAULT_RESPONSE_ERROR_MESSAGE } from 'modules/campaigns/lib/constants';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { useDispatch } from 'react-redux';
import { DestinationsFormValues } from '../context';
import { getWebhookUpsertVariables } from '../utils';
import { useGetWebhook } from './useGetWebhook';

export const editWebhookMutation = gql`
  mutation UpdateCampaignAlertsWebhookRequest(
    $id: Long!
    $name: String
    $enabled: Boolean
    $endpoint: String
    $authType: WebhookAuthType
    $authToken: String
    $clientId: String
    $clientSecret: String
    $tokenEndpoint: String
  ) {
    updateCampaignAlertsWebhook(
      id: $id
      name: $name
      enabled: $enabled
      endpoint: $endpoint
      authType: $authType
      authToken: $authToken
      clientId: $clientId
      clientSecret: $clientSecret
      tokenEndpoint: $tokenEndpoint
    ) {
      id
      createdAt
      updatedAt
      name
      enabled
      endpoint
      authType
      authToken
      clientId
      clientSecret
      tokenEndpoint
    }
  }
`;

export const useEditWebhook = (id: number | undefined, onComplete: () => void) => {
  const dispatch = useDispatch();
  const { refetch } = useGetWebhook(id);

  const [mutate, { data, loading: isLoading, error }] = useMutation(editWebhookMutation, {
    onCompleted: (response) => {
      const { name } = response?.updateCampaignAlertsWebhook;
      if (name) {
        dispatch(addPositiveToast(`${name} updated successfully.`));
        onComplete();
        refetch();
      }
    },
    onError: (e) => {
      const errorResponseMessage = maybeRestErrorMessage(e) || DEFAULT_RESPONSE_ERROR_MESSAGE;
      const errorMessage = `Cannot update webhook: ${errorResponseMessage}`;
      dispatch(addNegativeToastWithErrorId(errorMessage, 'useEditWebhook', e));
    },
  });

  const editWebhook = (
    formValues: DestinationsFormValues,
    dirtyFields: Record<string, boolean>,
  ) => {
    const variables = {
      ...getWebhookUpsertVariables({ formValues, isEdit: true, dirtyFields }),
      id,
    };
    mutate({ variables });
  };

  return {
    editWebhook,
    data,
    isLoading,
    errorMessage: error?.message || data?.updateCampaignAlertsWebhook?.failure?.message,
  };
};
